import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { MessageSquareText, Repeat2, Send, ThumbsUp } from "lucide-react";
import ImageView from "../ImageView";
import CanvaView from "../CanvaView";
import { getCurrentLinkedInMe } from "../../../api/AppLinkApi";
import { getBaseUrl } from "../../../api/api";
import { timeAgoShort } from "../../helpers";

interface Props {
  postType: string;
  content: string;
  scheduledDateTime: Date;
  mediaId: string;
  mediaType: "HOST" | "CANVA";
  engagement: any;
}

export default function LinkedInPost({
  postType,
  content,
  scheduledDateTime,
  mediaId,
  mediaType,
}: Props) {
  const { t } = useTranslation();

  const [displayName, setDisplayName] = useState(t("Anonymous"));
  const [avatarUrl, setAvatarUrl] = useState();

  useEffect(() => {
    loadProfile();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const loadProfile = () => {
    getCurrentLinkedInMe().then((data) => {
      setDisplayName(`${data?.given_name} ${data?.family_name}` || t("Anonymous"));
      setAvatarUrl(data?.picture);
    });
  };

  return (
    <div className="max-w-xl mx-auto bg-white border border-gray-200 rounded-2xl shadow-sm p-4 mt-6">
      {/* Header */}
      <div className="flex items-start space-x-3">
        {avatarUrl ? (
          <img src={avatarUrl} alt="avatar" className="w-12 h-12 rounded-full" />
        ) : (
          <div className="w-12 h-12 rounded-full">
            <svg className="h-full w-full text-gray-300" fill="currentColor" viewBox="0 0 24 24">
              <path d="M24 20.993V24H0v-2.996A14.977 14.977 0 0112.004 15c4.904 0 9.26 2.354 11.996 5.993zM16.002 8.999a4 4 0 11-8 0 4 4 0 018 0z" />
            </svg>
          </div>
        )}
        <div className="flex-1">
          <h2 className="font-semibold text-gray-900">{displayName}</h2>
          <p className="text-sm text-gray-500">Chief Executive Officer (CEO)</p>
          <span className="text-xs text-gray-400">{timeAgoShort(scheduledDateTime)} • 🌍</span>
        </div>
        <button className="text-gray-400 hover:text-gray-600">
          <svg
            xmlns="http://www.w3.org/2000/svg"
            className="h-5 w-5"
            viewBox="0 0 20 20"
            fill="currentColor"
          >
            <path d="M10 6a2 2 0 110-4 2 2 0 010 4zM10 12a2 2 0 110-4 2 2 0 010 4zM10 18a2 2 0 110-4 2 2 0 010 4z" />
          </svg>
        </button>
      </div>

      {/* Post Content */}
      <div className="mt-3 text-gray-800 text-[15px] leading-relaxed">{content}</div>

      {/* Image */}
      <div className="mt-3">
        {postType === "image" ? (
          <>
            {mediaId ? (
              <>
                {mediaType === "CANVA" ? (
                  <CanvaView fileId={mediaId} className="w-full h-full object-contain" />
                ) : (
                  <ImageView fileName={mediaId} className="w-full h-full object-contain" />
                )}
              </>
            ) : (
              <img
                src={`${getBaseUrl()}/static/images/upload-images.png`}
                alt=""
                className="w-full h-full object-cover"
              />
            )}
          </>
        ) : null}
      </div>

      {/* Reactions Summary */}
      <div className="mt-3 flex items-center justify-between text-sm text-gray-500">
        <div className="flex items-center space-x-1">
          <span>👍</span>
          <span>{0}</span>
        </div>
        <span>
          {0} comments • {0} reposts
        </span>
      </div>

      <hr className="my-2 text-gray-200" />

      {/* Actions */}
      <div className="flex justify-between text-gray-600 text-sm font-medium">
        <button className="flex items-center space-x-2 hover:text-blue-600">
          <ThumbsUp className="w-5 h-5" />
          <span>{t("Like")}</span>
        </button>
        <button className="flex items-center space-x-2 hover:text-blue-600">
          <MessageSquareText className="w-5 h-5" />
          <span>{t("Comment")}</span>
        </button>
        <button className="flex items-center space-x-2 hover:text-blue-600">
          <Repeat2 className="w-5 h-5" />
          <span>{t("Repost")}</span>
        </button>
        <button className="flex items-center space-x-2 hover:text-blue-600">
          <Send className="w-5 h-5" />
          <span>{t("Send")}</span>
        </button>
      </div>
    </div>
  );
}
